import { useEffect, useMemo, useState } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import WarehouseRoundedIcon from '@mui/icons-material/WarehouseRounded';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import {
  CompactTablePagination,
  EmptyState,
  ErrorState,
  LoadingState,
  SectionScaffold,
  SelectionBanner,
} from '@/shared/ui';

import { useBrowseProducts, useDeleteProduct, useSearchCategories } from '../../hooks';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { formatPrice } from '../../lib/format';
import { useToast } from '../../lib/useToast';
import {
  COMMERCIAL_AVAILABILITY_LABELS,
  PRODUCT_CARD_STATUS_LABELS,
  commercialAvailabilityColor,
  productCardStatusColor,
  STOCK_STATUS_LABELS,
  stockStatusColor,
} from '../../model/options';
import type {
  BrowseProductsParams,
  Product,
  ProductSearchRequest,
  ProductSummary,
  StockStatus,
} from '../../model/types';
import { BulkStockUpdateDialog } from '../components/BulkStockUpdateDialog';
import { ImportFlyout } from '../components/ImportFlyout';
import { ProductDetailDrawer } from '../components/ProductDetailDrawer';
import { QuickPriceDialog, QuickStockDialog } from '../components/ProductQuickEditDialogs';

// ── Filter / sort options ────────────────────────────────────────────────────

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'Все' },
  { value: 'DRAFT', label: 'Черновики' },
  { value: 'PUBLISHED', label: 'Опубликованные' },
  { value: 'ARCHIVED', label: 'В архиве' },
];

const BROWSE_SORT_OPTIONS = [
  { value: 'updatedAt,desc', label: 'Сначала изменённые' },
  { value: 'name,asc', label: 'Название (А–Я)' },
  { value: 'name,desc', label: 'Название (Я–А)' },
  { value: 'price,asc', label: 'Цена (по возрастанию)' },
  { value: 'price,desc', label: 'Цена (по убыванию)' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Map a master browse summary onto the Product shape the quick-edit dialogs expect. */
function summaryToProduct(s: ProductSummary): Product {
  return {
    id: s.id,
    name: s.name,
    code: s.code,
    categoryId: s.categoryId ?? '',
    categoryName: s.categoryName ?? '',
    brand: s.brand,
    price: s.price,
    oldPrice: s.oldPrice,
    imageUrl: s.imageUrl,
    stockStatus: (s.stockStatus ?? 'CHECK_AVAILABILITY') as StockStatus,
    // Pass status fields through so dialog context is accurate
    cardStatus: s.cardStatus,
    commercialAvailability: s.commercialAvailability,
  };
}

// ── Page ─────────────────────────────────────────────────────────────────────

/**
 * Merchant product catalogue — browses the FULL master catalog (drafts included)
 * with search, status/category filters, inline price & stock edits, and delete
 * (epic-catalog-002, story-024).
 *
 * This page is the **reference implementation** for table-format pages in the
 * merchant portal. Key shared patterns used here:
 *
 * - `SectionScaffold` — header + search + settings gear
 * - `SelectionBanner` — bulk-selection state bar
 * - `CompactTablePagination` — compact footer pagination with rows-per-page
 * - `stickyHeader` + `maxHeight` on `TableContainer` for scrollable tables
 * - Responsive column visibility (`display: { xs: 'none', sm: 'table-cell' }`)
 */
export function ProductsPage() {
  const navigate = useNavigate();
  const { canView, canManage } = useCatalogPermissions();
  const { toast, success, error: notifyError, clear } = useToast();

  // ── Filter & pagination state ──────────────────────────────────────────────
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState('updatedAt,desc');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  // ── Dialog / drawer targets ────────────────────────────────────────────────
  const [drawerProduct, setDrawerProduct] = useState<ProductSummary | null>(null);
  const [priceTarget, setPriceTarget] = useState<Product | null>(null);
  const [stockTarget, setStockTarget] = useState<Product | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProductSummary | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  // ── Bulk-selection state ───────────────────────────────────────────────────
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  // ── Debounce free-text search ──────────────────────────────────────────────
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setQuery(queryInput.trim());
      setPage(0);
    }, 350);
    return () => window.clearTimeout(handle);
  }, [queryInput]);

  // ── Data ───────────────────────────────────────────────────────────────────
  const categoriesQuery = useSearchCategories(
    { page: 0, size: 100, sort: 'name,asc' },
    { enabled: canView },
  );
  const deleteMutation = useDeleteProduct();

  const browseParams = useMemo<BrowseProductsParams>(
    () => ({
      query: query || undefined,
      status: status === 'all' ? undefined : (status as 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'),
      categoryId: categoryId || undefined,
      page,
      size,
      sort,
    }),
    [query, status, categoryId, sort, page, size],
  );

  const productsQuery = useBrowseProducts(browseParams, { enabled: canView });
  const products = productsQuery.data?.content ?? [];
  const total = productsQuery.data?.totalElements ?? 0;

  // Bulk search request (selectAllMode only — explicit IDs skip this).
  // NOTE: ProductSearchRequest cannot express the status dimension, so
  // selectAllMode is only activated when status === 'all' (see toggleSelectAll).
  const bulkSearchRequest = useMemo<ProductSearchRequest>(
    () => ({
      query: query || undefined,
      criteria: { categoryIds: categoryId ? [categoryId] : undefined },
      pagination: { page, size },
    }),
    [query, categoryId, page, size],
  );

  // ── Helpers ────────────────────────────────────────────────────────────────

  /** Reset to page 0 when any filter changes. */
  const resetPageAnd = (fn: () => void) => {
    fn();
    setPage(0);
  };

  // Clear selection whenever filters or sort change.
  useEffect(() => {
    setSelectedProductIds(new Set());
    setSelectAllMode(false);
  }, [query, categoryId, status, sort]);

  // ── Selection ──────────────────────────────────────────────────────────────

  const toggleProductSelection = (productId: string) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
        if (selectAllMode && next.size === 0) setSelectAllMode(false);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectAllMode) {
      setSelectAllMode(false);
      setSelectedProductIds(new Set());
    } else if (status === 'all') {
      // "Select all pages" mode is only safe when no status filter is active —
      // ProductSearchRequest can't express DRAFT/PUBLISHED/ARCHIVED, so the bulk
      // backend call would silently scope-spill into the wrong product set.
      setSelectAllMode(true);
      setSelectedProductIds(new Set(products.map((p) => p.id)));
    } else {
      // Status filter active → select current page by explicit IDs only.
      setSelectedProductIds(new Set(products.map((p) => p.id)));
    }
  };

  const clearSelection = () => {
    setSelectedProductIds(new Set());
    setSelectAllMode(false);
  };

  const isAllCurrentPageSelected =
    products.length > 0 && products.every((p) => selectAllMode || selectedProductIds.has(p.id));

  const selectedCount = selectAllMode ? total : selectedProductIds.size;

  // ── Delete ─────────────────────────────────────────────────────────────────

  const confirmDelete = () => {
    if (!deleteTarget) return;
    const name = deleteTarget.name;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        success(`Товар «${name}» удалён`);
        setDeleteTarget(null);
      },
      onError: (e) => {
        notifyError(e.message ?? 'Не удалось удалить товар');
        setDeleteTarget(null);
      },
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <SectionScaffold
      title="Товары"
      subtitle="Весь каталог товаров, включая черновики — поиск, фильтры, цены, остатки и публикация."
      canView={canView}
      settingsTo="/settings/catalog"
      settingsLabel="Настройки каталога"
      search={{
        value: queryInput,
        onChange: setQueryInput,
        placeholder: 'Поиск по названию, бренду, артикулу…',
      }}
      primaryAction={
        canManage ? (
          <>
            {selectedCount > 0 && (
              <Tooltip
                title={
                  selectAllMode
                    ? 'Обновить все товары в поиске'
                    : 'Обновить наличие для выбранных'
                }
              >
                <Button
                  variant="outlined"
                  startIcon={<WarehouseRoundedIcon />}
                  onClick={() => setBulkDialogOpen(true)}
                >
                  Наличие ({selectedCount})
                </Button>
              </Tooltip>
            )}
            <Button
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              onClick={() => setImportOpen(true)}
            >
              Импорт
            </Button>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={() => navigate('/catalog/products/new')}
            >
              Добавить товар
            </Button>
          </>
        ) : undefined
      }
    >
      <Stack spacing={2.5}>

        {/* ── Filter bar ────────────────────────────────────────────────── */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
          <TextField
            select
            size="small"
            label="Состояние"
            value={status}
            onChange={(e) => resetPageAnd(() => setStatus(e.target.value))}
            sx={{ minWidth: 180 }}
          >
            {STATUS_FILTER_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Категория"
            value={categoryId}
            onChange={(e) => resetPageAnd(() => setCategoryId(e.target.value))}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Все категории</MenuItem>
            {(categoriesQuery.data?.content ?? []).map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Сортировка"
            value={sort}
            onChange={(e) => resetPageAnd(() => setSort(e.target.value))}
            sx={{ minWidth: 200 }}
          >
            {BROWSE_SORT_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {/* ── Table area ────────────────────────────────────────────────── */}
        <Box>
          {/*
           * SelectionBanner — shared component for bulk-action workflows.
           * Appears above the table when ≥1 rows are checked.
           */}
          {selectedCount > 0 && (
            <SelectionBanner
              label={
                selectAllMode
                  ? `Выбраны все ${total} товаров в результатах поиска`
                  : `Выбрано товаров: ${selectedCount}`
              }
              onClear={clearSelection}
            />
          )}

          {/* Content states */}
          {productsQuery.isLoading ? (
            <LoadingState label="Загрузка товаров…" />
          ) : productsQuery.isError ? (
            <ErrorState
              title="Не удалось загрузить товары"
              message={productsQuery.error?.message ?? 'Произошла непредвиденная ошибка.'}
              onRetry={() => void productsQuery.refetch()}
            />
          ) : products.length === 0 ? (
            <EmptyState
              title={
                query || categoryId || status !== 'all' ? 'Ничего не найдено' : 'Товаров пока нет'
              }
              description={
                query || categoryId || status !== 'all'
                  ? 'Измените условия поиска или фильтры.'
                  : 'Добавьте первый товар или импортируйте каталог из файла.'
              }
              icon={<Inventory2RoundedIcon />}
              action={
                canManage ? (
                  <Button
                    variant="contained"
                    startIcon={<AddRoundedIcon />}
                    onClick={() => navigate('/catalog/products/new')}
                  >
                    Добавить товар
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              {/*
               * TableContainer with maxHeight + stickyHeader keeps the column
               * labels visible while scrolling a large result set. The pagination
               * sits inline below — always visible, no pinned footer needed.
               */}
              <TableContainer sx={{ maxHeight: { xs: 480, sm: 560, md: 640 } }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={!isAllCurrentPageSelected && selectedCount > 0}
                          checked={isAllCurrentPageSelected || selectAllMode}
                          onChange={toggleSelectAll}
                        />
                      </TableCell>
                      <TableCell>Товар</TableCell>
                      {/* Hidden on xs — screen too narrow for 6 columns */}
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        Категория
                      </TableCell>
                      <TableCell align="right">Цена</TableCell>
                      <TableCell>Состояние</TableCell>
                      <TableCell align="right">Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow
                        key={product.id}
                        hover
                        selected={selectAllMode || selectedProductIds.has(product.id)}
                        onClick={() => setDrawerProduct(product)}
                        sx={{ cursor: 'pointer' }}
                      >
                        {/*
                         * stopPropagation on checkbox cell prevents the row-click drawer
                         * from firing when the merchant is selecting rows for bulk actions.
                         */}
                        <TableCell
                          padding="checkbox"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ cursor: 'default' }}
                        >
                          <Checkbox
                            checked={selectAllMode || selectedProductIds.has(product.id)}
                            onChange={() => toggleProductSelection(product.id)}
                          />
                        </TableCell>

                        {/* Product name + code/brand */}
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar
                              variant="rounded"
                              src={product.imageUrl}
                              sx={{ width: 40, height: 40, flexShrink: 0, bgcolor: 'action.hover' }}
                            >
                              <Inventory2RoundedIcon fontSize="small" color="disabled" />
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {product.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {product.code ? (
                                  <Box
                                    component="span"
                                    sx={{ fontFamily: 'JetBrains Mono, monospace' }}
                                  >
                                    {product.code}
                                  </Box>
                                ) : (
                                  '—'
                                )}
                                {product.brand ? ` · ${product.brand}` : ''}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* Category — hidden on xs */}
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          <Typography variant="body2" color="text.secondary">
                            {product.categoryName ?? '—'}
                          </Typography>
                        </TableCell>

                        {/* Price + old price */}
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" fontWeight={600}>
                            {formatPrice(product.price)}
                          </Typography>
                          {product.oldPrice != null && product.oldPrice > (product.price ?? 0) ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textDecoration: 'line-through' }}
                            >
                              {formatPrice(product.oldPrice)}
                            </Typography>
                          ) : null}
                        </TableCell>

                        {/* Status chips — 3 dimensions: authoring, buyer-state, publication */}
                        <TableCell>
                          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                            {/*
                             * Authoring gate: cardStatus is the canonical storefront eligibility
                             * field. Backend default is ACTIVE (not DRAFT), so fall back to
                             * 'ACTIVE' when absent. Do NOT branch on lifecycleStatus.
                             */}
                            <Chip
                              size="small"
                              variant="outlined"
                              color={productCardStatusColor(product.cardStatus ?? 'ACTIVE')}
                              label={
                                PRODUCT_CARD_STATUS_LABELS[product.cardStatus ?? 'ACTIVE'] ??
                                'Активен'
                              }
                            />
                            {/*
                             * Buyer-state chip: commercialAvailability is PRIMARY — the backend
                             * ignores stockStatus when commercialAvailability is set. Show it
                             * first; fall back to stockStatus only when absent.
                             */}
                            {product.commercialAvailability ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                color={commercialAvailabilityColor(product.commercialAvailability)}
                                label={
                                  COMMERCIAL_AVAILABILITY_LABELS[
                                    product.commercialAvailability
                                  ] ?? product.commercialAvailability
                                }
                              />
                            ) : (
                              <Chip
                                size="small"
                                variant="outlined"
                                color={stockStatusColor(product.stockStatus)}
                                label={
                                  STOCK_STATUS_LABELS[product.stockStatus ?? ''] ??
                                  product.stockStatus ??
                                  '—'
                                }
                              />
                            )}
                            {/*
                             * Publication chip: warning only when product is ready to sell
                             * but not yet published. DRAFT/ARCHIVED → grey (expected state).
                             */}
                            {product.published ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                color="success"
                                label="Опубликован"
                              />
                            ) : (
                              <Chip
                                size="small"
                                variant="outlined"
                                color={
                                  (product.cardStatus === 'ACTIVE' ||
                                    product.cardStatus == null) &&
                                  product.commercialAvailability !== 'ARCHIVED'
                                    ? 'warning'
                                    : 'default'
                                }
                                label="Не опубликован"
                              />
                            )}
                          </Stack>
                        </TableCell>

                        {/* Row actions — stopPropagation so icon clicks don't open the detail drawer */}
                        <TableCell
                          align="right"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ cursor: 'default' }}
                        >
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Изменить цену">
                              {/* span wrapper required for Tooltip on a disabled button */}
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!canManage}
                                  aria-label={`Изменить цену: ${product.name}`}
                                  onClick={() => setPriceTarget(summaryToProduct(product))}
                                >
                                  <LocalOfferRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Наличие / остаток">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!canManage}
                                  aria-label={`Изменить остаток: ${product.name}`}
                                  onClick={() => setStockTarget(summaryToProduct(product))}
                                >
                                  <WarehouseRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={canManage ? 'Редактировать' : 'Открыть'}>
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label={`Редактировать: ${product.name}`}
                                onClick={() => navigate(`/catalog/products/${product.id}`)}
                              >
                                <EditRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canManage && (
                              <Tooltip title="Удалить">
                                <IconButton
                                  size="small"
                                  color="error"
                                  aria-label={`Удалить: ${product.name}`}
                                  onClick={() => setDeleteTarget(product)}
                                >
                                  <DeleteOutlineRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/*
               * CompactTablePagination — minimal-height footer row, directly below
               * the table (no separate pinned footer shell needed). Shows the
               * rows-per-page selector so merchants can adjust density.
               *
               * Reference: same visual format as ContentPagesPage > TablePagination.
               */}
              <CompactTablePagination
                count={total}
                page={page}
                rowsPerPage={size}
                rowsPerPageOptions={[10, 20, 50]}
                onPageChange={(_e, next) => setPage(next)}
                onRowsPerPageChange={(e) =>
                  resetPageAnd(() => setSize(parseInt(e.target.value, 10)))
                }
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
                sx={{ borderTop: (t) => `1px solid ${t.palette.divider}` }}
              />
            </>
          )}
        </Box>
      </Stack>

      {/* ── Product detail drawer ─────────────────────────────────────────── */}
      {/*
       * Opens on row click — shows product photo, readiness checklist
       * (cardStatus · commercialAvailability · published · price), and
       * secondary attributes. Quick-edit actions in the footer navigate to
       * the full editor or open the quick-edit dialogs without closing the drawer.
       */}
      <ProductDetailDrawer
        product={drawerProduct}
        open={drawerProduct !== null}
        canManage={canManage}
        onClose={() => setDrawerProduct(null)}
        onEdit={(id) => {
          setDrawerProduct(null);
          navigate(`/catalog/products/${id}`);
        }}
        onQuickPrice={(p) => setPriceTarget(p)}
        onQuickStock={(p) => setStockTarget(p)}
      />

      {/* ── Quick-edit dialogs ────────────────────────────────────────────── */}
      <QuickPriceDialog
        product={priceTarget}
        open={priceTarget !== null}
        onClose={() => setPriceTarget(null)}
        onResult={(severity, message) =>
          severity === 'success' ? success(message) : notifyError(message)
        }
      />
      <QuickStockDialog
        product={stockTarget}
        open={stockTarget !== null}
        onClose={() => setStockTarget(null)}
        onResult={(severity, message) =>
          severity === 'success' ? success(message) : notifyError(message)
        }
      />

      {/* ── Delete confirmation ───────────────────────────────────────────── */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Удалить товар?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Товар «{deleteTarget?.name}» будет удалён из каталога без возможности
            восстановления. Для временного снятия с витрины используйте архивацию.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Отмена</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleteMutation.isPending}
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk stock update ─────────────────────────────────────────────── */}
      <BulkStockUpdateDialog
        open={bulkDialogOpen}
        selectedCount={selectedCount}
        totalCount={total}
        selectAllMode={selectAllMode}
        selectedIds={selectedProductIds}
        searchRequest={bulkSearchRequest}
        onClose={() => setBulkDialogOpen(false)}
        onResult={(severity, message) => {
          if (severity === 'success') {
            success(message);
            clearSelection();
          } else {
            notifyError(message);
          }
        }}
      />

      {/* ── Import flyout ─────────────────────────────────────────────────── */}
      <ImportFlyout open={importOpen} onClose={() => setImportOpen(false)} />

      {/* ── Toast ─────────────────────────────────────────────────────────── */}
      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clear}
          severity={toast?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </SectionScaffold>
  );
}
